<template>

    <div :class="['filter-menu', 'filter-' + name ]">

        <div class="filter-label">
            {{ label }}
        </div>

        <div class="flex-grow-1 position-relative">
            <button @click.prevent="clickFilter" :class="['btn', 'w-100', 'btn-filter', {
                active: open, disabled: disabled}]">{{ display }}</button>
            <filter-dialog v-if="open" :name="name" :size="sizeClass">

                <p v-if="disabled" class="disclaimer text-muted text-center mb-2">Can't select {{ name}} while filtering by building.</p>

                <div class="filter-content filter-minmax">
                    <div class="filter-group">

                        <div class="filter-title">Minimum {{ name }}</div>

                        <div v-if="manual" class="filter-text-entry">
                            <input
                                v-model.lazy.trim="manualMinimum"
                                @change="applyManualMin(name, manualMinimum)"
                                class="form-control"
                                type="text"
                                maxlength="12"
                                placeholder="Minimum">
                        </div>

                        <div class="list-group">
                            <a v-for="opt in options.min" :key="opt.id"
                                href="#"
                                @click.prevent="applyMin(name, opt.id)"
                                :class="[
                                    'list-group-item',
                                    'list-group-item-action',
                                    {
                                        active: opt == selectedMinOption,
                                        disabled: disabled || (selected.max && opt.id >= selected.max)
                                    }]">

                                {{ opt.name }}
                            </a>
                        </div>
                    </div>

                    <div class="filter-group max">
                        <div class="filter-title">Maximum {{ name }}</div>

                        <div v-if="manual" class="filter-text-entry">
                            <input
                                v-model.lazy.trim="manualMaximum"
                                @change="applyManualMax(name, manualMaximum)"
                                class="form-control"
                                type="text"
                                maxlength="12"
                                placeholder="Maximum">
                        </div>

                        <div class="list-group">
                            <a v-for="opt in filteredMaxOptions" :key="opt.id"
                                href
                                @click.prevent="applyMax(name, opt.id)"
                                :class="['list-group-item', 'list-group-item-action', {
                                    active: opt == selectedMaxOption,
                                    disabled: disabled || (selected.min && opt.id < selected.min)
                                }]">
                                {{ opt.name }}
                            </a>

                            <!-- the "Any" option -->
                            <a href
                                @click.prevent="applyMax(name, null)"
                                :class="['list-group-item', 'list-group-item-action', {active: selectedMaxOption == null}]">
                                {{ maximumName }}
                            </a>

                        </div>
                    </div>
                </div>


            </filter-dialog>
        </div>
    </div>

</template>

<script>

import FilterMixin from '@/mls-filter/mixins/FilterMixin.js';
import { startCase } from 'lodash';

export default {

    mixins: [FilterMixin],

    props: {
        options: Object,
        selected: Object,
        manual: Boolean,
        // disabled: Boolean,
    },

    data() {
        return {
            manualMinimum: this.manual ? this.selected.min : null,
            manualMaximum: this.manual ? this.selected.max : null,
        };
    },

    computed: {

        sizeClass() {
            switch (this.name) {
                case 'price':
                    return 'wide';
                case 'age':
                    return 'medium';
                default:
                    return 'medium';
            }
        },

        filteredMaxOptions() {

            const min = this.selected.min;
            const max = this.selected.max;
            let maxList = this.options.max;


            if (this.name === 'price' && !min) {
                // custom initial max list for price
                const maxFilter = [
                    500000,
                    800000,
                    1000000,
                    1500000,
                    2000000,
                    2500000,
                    3000000,
                ];
                maxList = this.options.max.filter((n) => (maxFilter.includes(n.id)));
            } else if (this.name === 'price') {
                // filter maxList to only include values greater than the selected min
                maxList = this.options.max.filter((n) => (n.id > min || n.id === max));
            } else if (min) {
                // filter maxList to only include values greater OR EQUAL than the selected min
                maxList = this.options.max.filter((n) => (n.id >= min || n.id === max));
                // console.log(maxList);
            }

            switch (this.name) {
                case 'price':
                    maxList = maxList.slice(0, 7);
                    if (maxList.length) {
                        const top = maxList[maxList.length - 1].id;
                        if (max > top) {
                            maxList[maxList.length - 1] = this.selectedMaxOption;
                        }
                    }
                    return maxList;
                case 'beds':
                    // return all options for beds
                    return this.options.max;
                case 'age':
                    // return filtered list for age
                    return maxList;
                default:
                    // otherwise return filtered list
                    return maxList;
            }
        },

        selectedOption() {
            return new Error('Can\'t use selectedOption in filter-min-max. Use selectedMinOption or selectedMaxOption.');
        },

        selectedMinOption() {

            const min = this.getMinOption(this.selected.min);

            if (min != null) {
                return min;
            } else if (this.manualMinimum != null) {
                return {
                    id: this.manualMinimum,
                    short: this.numberWithCommas(this.manualMinimum),
                    name: '$' + this.numberWithCommas(this.manualMinimum),
                };
            } else {
                return this.getMinOption(0);
            }
        },

        selectedMaxOption() {

            const max = this.getMaxOption(this.selected.max);

            if (max) {
                return max;
            } else if (this.manualMaximum != null) {
                return {
                    id: this.manualMaximum,
                    short: this.manualMaximum,
                    name: '$' + this.manualMaximum,
                };
            } else {
                return null;
            }
        },

        selectedMinName() {
            return this.selectedMinOption.short || this.selectedMinOption.name;
        },

        selectedMaxName() {
            return this.selectedMaxOption.short || this.selectedMaxOption.name;
        },

        suffix() {
            switch (this.name) {
                case 'price':
                    return '';
                case 'age':
                    return ' YRS';
                default:
                    return ' ' + startCase(this.name);
            }
        },

        maximumName() {
            switch (this.name) {
                default:
                    return 'Any ' + this.name;
            }

        },

        /* Label for Minmax select filter */
        display() {

            if (this.selected.min && this.selected.max) {
                if (this.selected.min === this.selected.max) {
                    if (this.suffix.toLowerCase().endsWith('s') && this.selected.min === 1) {
                        return this.selectedMinName + this.suffix.slice(0, -1);
                    } else {
                        return this.selectedMinName + this.suffix;
                    }
                } else {
                    return this.selectedMinName + ' to ' + this.selectedMaxName + this.suffix;
                }
            } else if (this.selected.min) {
                return this.selectedMinName + '+' + this.suffix;
            } else if (this.selected.max) {
                return 'Up to ' + this.selectedMaxName + this.suffix;
            } else {
                return startCase(this.name);
            }
        },
    },

    methods: {
        numberWithCommas(x) {
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        getOption(id) {
            throw new Error('Can\'t use getOption in filter-min-max. Use getMinOption or getMaxOption.');
        },
        getMinOption(id) {
            return this.options.min.find((opt) => (opt.id === id));
        },
        getMaxOption(id) {
            return this.options.max.find((opt) => (opt.id === id));
        },
        applyManualMin(filter, min) {
            min = parseFloat(min.replace(/\D/g, ''));
            if (isNaN(min)) {
                min = 0;
            }
            this.manualMinimum = min.toLocaleString();
            this.$store.dispatch('applyFilterMin', {filter, min});
        },
        applyManualMax(filter, max) {
            max = parseFloat(max.replace(/\D/g, ''));
            if (isNaN(max)) {
                max = 0;
            }
            this.manualMaximum = max.toLocaleString();
            this.$store.dispatch('applyFilterMax', {filter, max});
        },
        applyMin(filter, min) {
            if (!min) {
                this.manualMinimum = null;
            } else {
                this.manualMinimum = min.toLocaleString();
            }
            this.$store.dispatch('applyFilterMin', {filter, min});
        },
        applyMax(filter, max) {
            if (!max) {
                this.manualMaximum = null;
            } else {
                this.manualMaximum = max.toLocaleString();
            }
            this.$store.dispatch('applyFilterMax', {filter, max});
        },
    },
};

</script>